import { BuybackInCart } from "@/apps/shared/buyback/core/domain/model/buybackInCart/BuybackInCart";
import { CustomerInfo } from "@/apps/shared/buyback/core/domain/service/DTO/customerInfo/customerInfo";
import { CustomerIdentification } from "@/apps/shared/buyback/core/domain/service/DTO/customerInfo/customerIdentification";
import { PaymentMethod } from "@/apps/shared/proposal/core/domain/model/PaymentMethod";
import { v4 as uuidv4 } from "uuid";

export class Cart {
  public cartId: string;
  public cartBuybacks: BuybackInCart[];
  public customerInfo: CustomerInfo | null;
  public customerIdentification: CustomerIdentification | null;
  public readonly maxNbOfItems: number;
  public readonly minimumCartValue: number;
  public preDeclaredBuybackId: string | null;

  constructor(cartId: string, cartBuybacks: BuybackInCart[], maxNbOfItems: number, minimumCartValue: number) {
    this.cartId = cartId;
    this.cartBuybacks = cartBuybacks;
    this.customerInfo = null;
    this.customerIdentification = null;
    this.maxNbOfItems = maxNbOfItems;
    this.minimumCartValue = minimumCartValue;
    this.preDeclaredBuybackId = null;
  }

  getTotalPriceWithModifiersAsDecimal(): number {
    if (this.isEmpty()) {
      return 0;
    }

    const prices = this.cartBuybacks.map((buybackInCart) => {
      return buybackInCart.getTotalPriceAsDecimal();
    });

    return prices.reduce((previousValue: number, currentValue: number) => previousValue + currentValue, 0);
  }

  getTotalPriceWithoutModifiersAsDecimal(): number {
    if (this.isEmpty()) {
      return 0;
    }

    const prices = this.cartBuybacks.map((buybackInCart) => buybackInCart.estimatedPrice);

    return prices.reduce((previousValue: number, currentValue: number) => previousValue + currentValue, 0);
  }

  getPriceImpactsTotalAsDecimal(): number {
    if (this.isEmpty()) {
      return 0;
    }

    let amount = 0;

    this.cartBuybacks.map((buybackInCart) => {
      amount += buybackInCart.getModifiersAmountAsDecimal();
    });

    return amount;
  }

  isEmpty(): boolean {
    return this.cartBuybacks.length === 0;
  }

  isFull(): boolean {
    return this.cartBuybacks.length >= this.maxNbOfItems;
  }

  getPaymentModes(): PaymentMethod[] {
    return this.cartBuybacks.map((buybackInCart) => buybackInCart.paymentMode);
  }

  addBuyback(buyback: BuybackInCart): void {
    // Remove unnecessary (and potentially heavy) attributes
    delete buyback.transportMode.circuit?.transport?.allowedCategoryIds;
    delete buyback.transportMode.circuit?.transport?.categoryIds;

    this.cartBuybacks.push(buyback);
  }

  removeBuyback(buybackIndex: number): void {
    this.cartBuybacks.splice(buybackIndex, 1);
  }

  setCustomerInfo(customerInfo: CustomerInfo): void {
    this.customerInfo = customerInfo;
  }

  setCustomerIdentification(customerIdentification: CustomerIdentification): void {
    this.customerIdentification = customerIdentification;
  }

  getMissingForMinimumValueAsAbsoluteDecimal(): number {
    return this.minimumCartValue !== 0
      ? Math.abs(this.getTotalPriceWithModifiersAsDecimal() - this.minimumCartValue)
      : 0;
  }

  isMinimumCartValueReached(): boolean {
    return this.getTotalPriceWithModifiersAsDecimal() >= this.minimumCartValue;
  }

  reset(): void {
    this.cartId = uuidv4();
    this.cartBuybacks = [];
    this.customerInfo = null;
    this.customerIdentification = null;
    this.preDeclaredBuybackId = null;
  }

  setPreDeclaredBuybackId(preDeclaredBuybackId: string): void {
    this.preDeclaredBuybackId = preDeclaredBuybackId;
  }
}
